/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * הבורר שמוצג כשהערוץ שהוכח מוביל ליותר מחשבון אחד.
 *
 * **סשן ננעל על לקוח אחד, תמיד.** זה אינו מסך שמחבר לכמה חשבונות אלא שלב ביניים -
 * אחרי שההוכחה הושלמה ולפני שנוצר סשן - שבו הלקוח בוחר לאן להיכנס. הבחירה נאכפת
 * בשרת מול שאילתה טרייה, ולא מול מה שהמסך מחזיק.
 *
 * ## למה חלון מלא, ולא רשימה בתוך הטופס
 *
 * בגרסה הראשונה הכרטיסים נערמו בתוך עמודת הטופס. עם שני חשבונות זה עבד, ועם שמונה
 * זה הפך את **העמוד כולו** לגלילה בעמודה ברוחב 325 - הכותרת נעלמה למעלה, ולא היה
 * שום מקום שאומר מה עושים כאן. בעיצוב הסלולרי, שבו העמודה היא כרטיס צר על רקע צבעוני,
 * זה נראה כמו תקלה.
 *
 * הבחירה הזו היא **המסך כולו** ברגע שהיא מוצגת - היא כל מה שנותר לעשות - ולכן היא
 * מקבלת את המסך: כותרת קבועה, רשימה שגוללת **בתוך עצמה**, ותחתית קבועה. `createPortal`
 * לגוף המסמך ולא `position: fixed` במקום: אב עם `transform` או `filter` יוצר הקשר
 * מיקום חדש ושובר `fixed` בלי שום סימן.
 *
 * ## הבורר אינו מעוצב פר ערכה, ובכוונה
 *
 * זהו משטח לבן מעל שתי הערכות. הן נבדלות בכרטיס ההתחברות, וברגע הזה אין כרטיס - יש
 * החלטה. חלון בצבעי הערכה היה מכריח את כרטיסי החשבונות להיות לבנים על צבע, כלומר
 * בדיוק הבעיה שהוא בא לפתור.
 *
 * ## מה מוצג, ומה לא
 *
 * שני חשבונות שחולקים טלפון הם לעיתים קרובות שני **אנשים** - משפחה, או חברה ובעליה -
 * וההוכחה שסופקה היא על הערוץ המשותף בלבד. לכן אין כאן כתובת מלאה ואין מייל מלא:
 * המיסוך נעשה בבילינג, ומה שלא יצא ממנו אינו יכול לדלוף מכאן.
 *
 * **והערוץ שהוכח אינו מוצג כלל.** הוא זהה בכל השורות - זו הסיבה שהן כאן - ולכן
 * `***2222` חמש פעמים הוא רעש שמסתיר את מה שכן מבדיל.
 */

/** מעל כמה חשבונות מוצג שדה סינון. מתחת לזה הרשימה נסרקת בעין. */
const FILTER_THRESHOLD = 6;

export default function AccountChooser({ accounts, channel, onChoose, onCancel, busyRef, disabled }) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const panelRef = useRef(null);

    /**
     * `Escape` סוגר, וגלילת הרקע ננעלת.
     *
     * בלי הנעילה הגלילה "בורחת" לעמוד שמאחור ברגע שהרשימה מגיעה לקצה - וזו בדיוק
     * ההתנהגות שהופכת חלון מלא למשהו שנראה שבור במובייל.
     */
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = (e) => {
            if (e.key === 'Escape' && onCancel && !disabled) onCancel();
        };
        document.addEventListener('keydown', onKeyDown);
        // הפוקוס על החלון, כדי שקורא מסך יכריז עליו וש-`Escape` ייקלט מיד.
        panelRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onCancel, disabled]);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return accounts;
        return accounts.filter((account) =>
            [account.name, account.city, account.email, account.phone, String(account.customerId)]
                .filter(Boolean)
                .some((field) => String(field).toLowerCase().includes(needle))
        );
    }, [accounts, query]);

    /** הערוץ שהוכח זהה בכל השורות, ולכן אינו מבדיל - ואינו מוצג. */
    const showEmail = channel !== 'EMAIL';
    const showPhone = channel !== 'PHONE';

    return createPortal(
        <div className='account-chooser-overlay' role='dialog' aria-modal='true'>
            <div className='account-chooser-panel' ref={panelRef} tabIndex={-1}>
                <div className='account-chooser-head'>
                    <h2 className='account-chooser-title'>{t('Choose the account you want to enter')}</h2>
                    <p className='account-chooser-hint'>
                        {t('These accounts share the contact detail you just confirmed. An account that is not verified with it will ask for one more verification.')}
                    </p>
                    {accounts.length > FILTER_THRESHOLD && (
                        <input
                            className='account-chooser-filter'
                            type='search'
                            value={query}
                            placeholder={t('Search by name or account number')}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    )}
                </div>

                <div className='account-chooser-list'>
                    {filtered.map((account) => (
                        <button
                            key={account.ref}
                            type='button'
                            className='account-card'
                            disabled={disabled}
                            aria-busy={busyRef === account.ref ? 'true' : undefined}
                            onClick={() => onChoose(account.ref)}
                        >
                            <span className='account-card-name'>
                                {account.name || t('Account')}
                                {/*
                                    **מה יידרש מהחשבון הזה - לפני הלחיצה ולא אחריה.**

                                    שתי השורות בבורר נראות זהות, ואינן: באחת הערוץ
                                    שהוכח כאן כבר מאומת, ובשנייה הוא לא - והבחירה בה
                                    תוביל לאימות נוסף. בורר שאינו אומר את זה מראש הוא
                                    בורר שמפתיע דווקא את מי שבחר נכון.
                                */}
                                <span
                                    className={
                                        account.needsCompletion
                                            ? 'account-card-badge account-card-badge-pending'
                                            : 'account-card-badge account-card-badge-verified'
                                    }
                                >
                                    {account.needsCompletion
                                        ? t('Requires an extra verification')
                                        : t('Verified')}
                                </span>
                                {account.isActive === false && (
                                    <span className='account-card-badge'>{t('Suspended')}</span>
                                )}
                            </span>
                            {/* מספר החשבון והיישוב בשורה אחת - שניהם קצרים, ושורה
                                לכל אחד הופכת כרטיס של שלוש עובדות לגובה של חמש. */}
                            <span className='account-card-line'>
                                {t('Account number')}: {account.customerId}
                                {account.city ? ` · ${account.city}` : ''}
                            </span>
                            {/* `dir="ltr"` כי טקסט לטיני בתוך שורה עברית מוצג בסדר
                                שאיש אינו מתכוון אליו. בלי תווי כיווניות בלתי נראים. */}
                            {showEmail && account.email && (
                                <span className='account-card-line' dir='ltr'>{account.email}</span>
                            )}
                            {showPhone && account.phone && (
                                <span className='account-card-line' dir='ltr'>{account.phone}</span>
                            )}
                            {busyRef === account.ref && <span className='button-spinner' />}
                        </button>
                    ))}

                    {!filtered.length && (
                        <p className='account-chooser-empty'>{t('No account matches this search')}</p>
                    )}
                </div>

                {onCancel && (
                    <div className='account-chooser-foot'>
                        <button
                            type='button'
                            className='ql-link'
                            disabled={disabled}
                            onClick={onCancel}
                        >
                            {t('Start over')}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}
